import React from 'react';
import { MessageCircle, Calendar, CheckCircle, Shield, Clock, Tag, Home, Archive, Layers } from 'lucide-react';
import ServicePageLayout from '../components/ServicePageLayout';

const ATTIC_BEFORE = 'https://images.unsplash.com/photo-1768050197707-5576b319e2d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const ATTIC_AFTER = 'https://images.unsplash.com/photo-1571568168521-b439c1162480?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';

const data = {
  seo: {
    title: 'Dachbodenräumung Wien » Fixpreis ✓ Alles weg – Dachboden entrümpeln Wien',
    description: 'Professionelle Dachbodenräumung Wien ✓ Schwere Möbel, Kisten, Jahrzehnte an Gerümpel ✓ Fixpreis ✓ Besenreine Übergabe. Dachbodenauflösung Wien – Termin noch diese Woche.',
    keywords: 'Dachbodenräumung Wien, Dachboden räumen Wien, Dachbodenauflösung Wien, Dachboden entrümpeln Wien, Dachboden entleeren Wien',
    canonical: '/dachbodenraeumung-wien',
    h1short: 'Dachbodenräumung Wien',
  },
  schemas: [{
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Dachbodenräumung Wien',
    'provider': { '@type': 'LocalBusiness', 'name': 'Wohnungsräumung Wien' },
    'areaServed': 'Wien',
    'description': 'Professionelle Dachbodenräumung in Wien. Alte Möbel, Kisten und Jahrzehnte an Gerümpel – fachgerecht abtransportiert und entsorgt. Fixpreisgarantie und besenreine Übergabe.',
    'offers': { '@type': 'Offer', 'price': '290', 'priceCurrency': 'EUR' },
  }],
  hero: {
    badge: 'Termine kurzfristig möglich',
    headline: (
      <>
        <span>Dachbodenräumung<br />Wien</span>
        <br />
        <span className="text-[#E8722A]">zum Fixpreis.</span>
      </>
    ),
    subline: 'Jahrzehnte oben. Ein Tag bei uns. Fertig.',
    bullets: [
      'Vollständig geräumt – egal wie voll, wie schwer, wie eng',
      'Fixpreis nach Besichtigung – keine bösen Überraschungen',
      'Alte Möbel, Kisten, Sperrmüll – wir tragen alles runter',
      'Wertanrechnung: Brauchbares wird direkt vom Preis abgezogen',
      'Besenreine Übergabe. Immer. Ohne Aufpreis.',
    ],
    imageSrc: ATTIC_BEFORE,
    imageAlt: 'Vollgestellter Dachboden Wien – vor der Räumung',
  },
  formTitle: 'Dachbodenräumung anfragen –',
  serviceName: 'Dachbodenräumung',
  serviceCard: {
    heading: 'Dachbodenräumung Wien – wir räumen, was jahrelang oben lag',
    paragraphs: [
      'Jahrzehntelang angesammelt. Kisten, alte Möbel, Kartons, Sperrholz, Hausrat – der Dachboden wird irgendwann zum unlösbaren Problem. Beim Verkauf. Bei der Renovierung. Oder einfach, weil es jetzt an der Zeit ist.',
      'Wir räumen Dachböden in Wien und Umgebung – vollständig, schnell und mit fairer Wertanrechnung für alles, was noch brauchbar ist. Einmal anrufen. Wir kommen, schauen alles an und nennen Ihnen einen verbindlichen Preis. Dann räumen wir.',
    ],
    listItems: [
      'Alte Möbel, Regale, Truhen, Kartons – alles kommt runter',
      'Auch schwere und sperrige Gegenstände über enge Treppen',
      'Wertanrechnung für gut erhaltene Stücke – direkt vom Preis abgezogen',
      'Fachgerechte und sortenreine Entsorgung aller Materialien',
      'Besenreine Übergabe des Dachbodens an den Eigentümer',
      'Kurzfristige Termine – auch bei Erbschaft oder Hausverkauf',
    ],
    imageSrc: ATTIC_AFTER,
    imageAlt: 'Geräumter, leerer Dachboden Wien – besenrein',
  },
  benefits: [
    { icon: <Shield size={22} />, title: 'Fixpreisgarantie', desc: 'Kein Stundenlohn, keine Überraschung. Verbindlicher Festpreis nach Besichtigung – egal wie viel noch oben ist.' },
    { icon: <Clock size={22} />, title: 'Schnelle Umsetzung', desc: 'Oft noch diese Woche. Bei Hausverkauf oder Erbschaft reagieren wir bevorzugt und schnell.' },
    { icon: <Tag size={22} />, title: 'Wertanrechnung', desc: 'Gut erhaltene Möbel, Antiquitäten oder Elektrogeräte werden bewertet und direkt vom Preis abgezogen.' },
    { icon: <CheckCircle size={22} />, title: 'Besenreine Übergabe', desc: 'Der Dachboden wird nach der Räumung sauber übergeben. Standard – ohne Aufpreis.' },
  ],
  process: [
    { icon: <MessageCircle size={24} />, title: 'Anfrage senden', desc: 'Formular ausfüllen oder kurz anrufen – 2 Minuten. Keine Verpflichtung, kein Risiko.' },
    { icon: <Calendar size={24} />, title: 'Besichtigung & Fixpreis', desc: 'Wir kommen vorbei, schauen uns alles an und geben Ihnen noch heute einen verbindlichen Festpreis.' },
    { icon: <CheckCircle size={24} />, title: 'Räumung & Übergabe', desc: 'Unser Team räumt den Dachboden komplett – inklusive Entsorgung und besenreiner Übergabe.' },
  ],
  pricing: [
    { label: 'Kleiner Dachboden', price: 'ab €290', icon: <Home size={20} />, highlight: false },
    { label: 'Mittlerer Dachboden', price: 'ab €490', icon: <Layers size={20} />, highlight: true },
    { label: 'Großer / Haus-Dachboden', price: 'ab €890', icon: <Archive size={20} />, highlight: false },
  ],
  faq: [
    { q: 'Was kostet eine Dachbodenräumung in Wien?', a: 'Kleine Dachböden ab €290, mittlere ab €490, große Hausdachböden ab €890. Nach der kostenlosen Besichtigung erhalten Sie einen verbindlichen Fixpreis – kein Stundenlohn, keine Überraschungen.' },
    { q: 'Können Sie auch schwere Möbel über enge Treppen tragen?', a: 'Ja. Wir sind darauf eingestellt. Enge Stiegen, niedrige Durchgänge, schwere Schränke – das ist unser Alltag. Wir kalkulieren das bereits beim Fixpreis mit ein.' },
    { q: 'Was passiert mit noch brauchbaren Gegenständen?', a: 'Gut erhaltene Möbel, Hausrat oder Antiquitäten werden vor Ort bewertet und direkt vom Gesamtpreis abgezogen. Wertanrechnung nennt sich das – und sie spart Ihnen bares Geld.' },
    { q: 'Wie lange dauert eine Dachbodenräumung?', a: 'Ein kleiner Dachboden ist oft in einem halben Tag fertig. Größere dauern einen vollen Tag. Wir räumen in einem Zug durch und hören nicht auf, bis alles weg ist.' },
    { q: 'Muss ich bei der Räumung dabei sein?', a: 'Nicht zwingend. Viele Kunden übergeben uns einfach den Schlüssel. Wir arbeiten sorgfältig und übergeben den geräumten Dachboden besenrein zurück.' },
    { q: 'Räumen Sie auch Dachböden in Mehrparteienhäusern?', a: 'Ja. Wir räumen Dachböden in Zinshäusern, Altbauten, Mehrfamilienhäusern und Einfamilienhäusern in Wien und Umgebung – termingerecht und diskret.' },
  ],
  relatedServices: [
    { title: 'Kellerräumung', slug: '/kellerraeumung-wien', icon: <Archive size={18} /> },
    { title: 'Wohnungsräumung', slug: '/wohnungsraeumung-wien', icon: <Home size={18} /> },
    { title: 'Haushaltsauflösung', slug: '/haushaltsaufloesung-wien', icon: <CheckCircle size={18} /> },
    { title: 'Nachlassräumung', slug: '/nachlassraeumung-wien', icon: <Layers size={18} /> },
  ],
};

export default function DachbodenRaeumungPage() {
  return <ServicePageLayout data={data} />;
}
